import React, { Dispatch, FC, SetStateAction, useEffect } from 'react';

import CardTitle from '@components/dashboard/common/CardTitle';
import CentinelButton from '@components/dashboard/common/CentinelButton';
import FormErrorField from '@components/dashboard/common/FormErrorField';

import { Button, Card, Col, Form, Input } from 'antd';

import { UserType } from '../SignUp';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import * as yup from 'yup';

interface SignUpFormProps {
  onSubmitHandler: (data: SignUpFormData) => void;
  outerError: string;
  changeOuterError: Dispatch<SetStateAction<string>>;
  userType: string;
}

export interface SignUpFormData {
  email: string;
  password: string;
  confirmPassword: string;
}

const schema = yup.object().shape({
  password: yup
    .string()
    .trim()
    .min(
      9,
      'Please create a password of at least 9 characters, with at least 1 lowercase, uppercase, number, and special character (e.g. !,*,#, etc.).',
    )
    .max(255)
    .matches(
      /^(?=.*[A-Z])/,
      'Error: Must contain at least one uppercase character',
    )
    .matches(
      /^(?=.*[a-z])/,
      'Error: Must contain at least one lowercase character',
    )
    .matches(
      /^(?=.*[0-9])/,
      'Error: Must contain at least one number character',
    )
    .matches(
      /^(?=.*[ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~])/,
      'Error: Must contain at least one special character',
    )
    .required(
      'Please create a password of at least 9 characters, with at least 1 lowercase, uppercase, number, and special character (e.g. !,*,#, etc.).',
    ),
  email: yup
    .string()
    .email('Error: please enter a valid email address')
    .required(
      'Please enter the email address you would like to use as your username',
    ),
  confirmPassword: yup
    .string()
    .trim()
    .required("Passwords don't match")
    .oneOf(
      [yup.ref('password'), null],
      'Passwords do not match. Please retype.',
    ),
});

const SignUpForm: FC<SignUpFormProps> = ({
  onSubmitHandler,
  outerError,
  changeOuterError,
  userType,
}) => {
  const {
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors, isValid, touchedFields },
  } = useForm<SignUpFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get('type');
  if (type && Object.values(UserType).includes(type as UserType)) {
    localStorage.setItem('userType', type);
  }

  const formTitle =
    userType === UserType.PRODUCER
      ? 'Producer Sign Up'
      : userType === UserType.AFFILIATE
      ? 'Affiliate Sign Up'
      : 'Purchaser Sign Up';

  const handleUsernameError = () => {
    if (outerError === 'user_already_has_policyholder_profile') {
      return (
        <span className="text-red">
          The email address you entered is already registered for a policyholder
          account. Please enter the another email address. If you need
          assistance, contact
          <a
            href={'mailto:help@yourcentinel.com'}
            target="_blank"
            rel="noreferrer"
            className="cursor-pointer"
          >
            {' '}
            help@yourcentinel.com
          </a>
          .
        </span>
      );
    }
    if (outerError === 'user_already_has_producer_profile') {
      return (
        <span className="text-red">
          The email address you entered is already registered for a producer
          account. Please enter the another email address. If you need
          assistance, contact
          <a
            href={'mailto:help@yourcentinel.com'}
            target="_blank"
            rel="noreferrer"
            className="cursor-pointer"
          >
            {' '}
            help@yourcentinel.com
          </a>
          .
        </span>
      );
    }
    if (outerError === 'user_already_has_affiliate_profile') {
      return (
        <span className="text-red">
          The email address you entered is already registered for an affiliate
          account. Please enter another email address. If you need assistance,
          contact
          <a
            href={'mailto:help@yourcentinel.com'}
            target="_blank"
            rel="noreferrer"
            className="cursor-pointer"
          >
            {' '}
            help@yourcentinel.com
          </a>
          .
        </span>
      );
    }
    if (outerError) {
      return (
        <span className="text-red">
          Error: the email address you entered is already associated with an
          existing account. Please
          <NavLink to={'/sign-in'}> Sign In</NavLink> or click
          <NavLink to={'/forgot-password'}> Reset Password</NavLink> if you`ve
          forgotten your login details.
        </span>
      );
    }
    return errors.email?.message;
  };

  useEffect(() => {
    const autoCompleteSubscription = watch((value, { name }) => {
      changeOuterError('');
    });
    return () => autoCompleteSubscription.unsubscribe();
  }, [watch, changeOuterError]);

  return (
    <Card
      title={<CardTitle level={2}>{formTitle}</CardTitle>}
      className="w-full sm:w-[600px] mx-auto px-4 sm:px-6 text-center bg-white"
    >
      <Form
        name="basic"
        labelCol={{
          span: 24,
          sm: { span: 6 },
        }}
        wrapperCol={{
          span: 24,
          sm: { span: 18 },
        }}
        initialValues={{ remember: true }}
        onFinish={handleSubmit(onSubmitHandler)}
        autoComplete="off"
        className="text-justify leading-tight"
      >
        <Form.Item
          label={<label className="text-blue">Email</label>}
          name="email"
          validateStatus={
            (errors.email || outerError) && touchedFields.email
              ? 'error'
              : undefined
          }
          help={
            touchedFields.email && (
              <FormErrorField>{handleUsernameError()}</FormErrorField>
            )
          }
        >
          <Controller
            name="email"
            control={control}
            render={({ field: { onBlur, ...fieldProps } }) => (
              <Input
                {...fieldProps}
                placeholder="Please enter your email address"
                onBlur={(e) => {
                  onBlur();
                  trigger('email');
                }}
              />
            )}
          />
        </Form.Item>
        <Form.Item
          label={<label className="text-blue">Password</label>}
          name="password"
          validateStatus={
            (errors.password || outerError) && touchedFields.password
              ? 'error'
              : undefined
          }
          help={
            touchedFields.password && (
              <FormErrorField>{errors.password?.message}</FormErrorField>
            )
          }
        >
          <Controller
            name="password"
            control={control}
            render={({ field: { onBlur, ...fieldProps } }) => (
              <Input.Password
                {...fieldProps}
                placeholder="Please create your password"
                onBlur={(e) => {
                  onBlur();
                  trigger('password');
                }}
              />
            )}
          />
        </Form.Item>
        <Form.Item
          label={<label className="text-blue">Confirm password</label>}
          name="confirmPassword"
          validateStatus={
            (errors.confirmPassword || outerError) &&
            touchedFields.confirmPassword
              ? 'error'
              : undefined
          }
          help={
            touchedFields.confirmPassword && (
              <FormErrorField>{errors.confirmPassword?.message}</FormErrorField>
            )
          }
        >
          <Controller
            name="confirmPassword"
            control={control}
            render={({ field: { onBlur, ...fieldProps } }) => (
              <Input.Password
                {...fieldProps}
                placeholder="Please retype your password"
                onBlur={(e) => {
                  onBlur();
                  trigger('confirmPassword');
                }}
              />
            )}
          />
        </Form.Item>
        <Col
          xs={{ span: 24, offset: 0 }}
          sm={{ span: 20, offset: 6 }} className="mb-6">
          <NavLink to="/sign-in">
            <Button type="link" className="text-sm px-0">
              Sign In
            </Button>
          </NavLink>
        </Col>
        <Form.Item
          wrapperCol={{
            xs: { offset: 0, span: 24 },
            sm: { offset: 6, span: 16 },
          }}
        >
          <CentinelButton
            type="default"
            htmlType="submit"
            disabled={!isValid || !!outerError}
          >
            Create my account
          </CentinelButton>
        </Form.Item>
        <Outlet />
      </Form>
    </Card>
  );
};

export default SignUpForm;
